import { useCallback, useEffect, useState } from "react";
import { RELEASES, LATEST_RELEASE_ID } from "@/data/releaseNotes";

// ONLY used for release-note read status. Never stores auth/session data.
export const LAST_SEEN_STORAGE_KEY = "postmoro_last_seen_release";
export const BANNER_DISMISSED_STORAGE_KEY = "postmoro_update_banner_dismissed";

function readLastSeen() {
  try {
    return typeof window !== "undefined"
      ? window.localStorage.getItem(LAST_SEEN_STORAGE_KEY) || null
      : null;
  } catch (_e) {
    return null;
  }
}

function writeLastSeen(id) {
  try {
    if (typeof window === "undefined") return;
    if (id) window.localStorage.setItem(LAST_SEEN_STORAGE_KEY, id);
  } catch (_e) {
    /* localStorage may be disabled — silently ignore */
  }
}

function readBannerDismissed() {
  try {
    return typeof window !== "undefined"
      ? window.sessionStorage.getItem(BANNER_DISMISSED_STORAGE_KEY) || null
      : null;
  } catch (_e) {
    return null;
  }
}

function writeBannerDismissed(id) {
  try {
    if (typeof window === "undefined") return;
    if (id) window.sessionStorage.setItem(BANNER_DISMISSED_STORAGE_KEY, id);
  } catch (_e) {
    /* ignore */
  }
}

/**
 * Read/write helper for the What's New unread indicator + update banner.
 *
 * Behavior:
 *   - `hasUnread` is true when the latest release id differs from the
 *     value stored under localStorage["postmoro_last_seen_release"].
 *   - `markLatestSeen()` persists the latest id so the indicator hides.
 *   - `bannerVisible` also honors a per-session dismiss in sessionStorage
 *     so the banner is not repeated after the user closes it (even before
 *     they visit the What's New page).
 */
export function useReleaseNotes() {
  const [lastSeen, setLastSeen] = useState(() => readLastSeen());
  const [bannerDismissed, setBannerDismissed] = useState(() => readBannerDismissed());

  // Keep local state in sync if another tab updates the value.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e) => {
      if (e.key === LAST_SEEN_STORAGE_KEY) setLastSeen(e.newValue || null);
      if (e.key === BANNER_DISMISSED_STORAGE_KEY) setBannerDismissed(e.newValue || null);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const latestId = LATEST_RELEASE_ID;
  const hasUnread = Boolean(latestId) && lastSeen !== latestId;
  const bannerVisible = hasUnread && bannerDismissed !== latestId;

  const markLatestSeen = useCallback(() => {
    if (!latestId) return;
    writeLastSeen(latestId);
    setLastSeen(latestId);
    // Opening What's New should also silence the banner for this release.
    writeBannerDismissed(latestId);
    setBannerDismissed(latestId);
  }, [latestId]);

  const dismissBanner = useCallback(() => {
    if (!latestId) return;
    writeBannerDismissed(latestId);
    setBannerDismissed(latestId);
  }, [latestId]);

  return {
    releases: RELEASES,
    latestRelease: RELEASES[0] || null,
    latestId,
    hasUnread,
    bannerVisible,
    markLatestSeen,
    dismissBanner,
  };
}
